import { ArrowDownOutlined, ArrowUpOutlined, LoadingOutlined } from "@ant-design/icons"
import React, { useEffect } from "react"
import { FC } from "react"
import { Pagination as AntPagination, Select } from "antd"

import styles from "./XTable.module.scss"

export type XTableExtensions = {
  Table: typeof Table
  Head: typeof Head
  Body: typeof Body
  Row: typeof Row
  Loading: typeof Loading
  Bottom: typeof Bottom
}

export type BottomExtensions = {
  Pagination: typeof Pagination
  Limit: typeof Limit
}

export type HeadExtensions = {
  Col: typeof Col
}

export type RowExtensions = {
  Col: typeof RowCol
}

export type XTableProps = {
  children: any
  light?: boolean
  isLoading?: boolean
  theme?: string
  style?: any
}

type TableProps = {
  children: any
  light?: boolean
  isLoading?: boolean
  order?: string
  orderBy?: string
  onOrderChange?: (column: string, order: "ASC" | "DESC") => void
}

type HeadProps = {
  children?: any
  light?: boolean
  isLoading?: boolean
  order?: string
  orderBy?: string
  cols?: string[]
  onOrderChange?: (column: string, order: "ASC" | "DESC") => void
}

type HeadColProps = {
  children: any
  onClick?: (ev: any) => void
  order?: string
  orderBy?: string
  onOrderChange?: (column: string, order: "ASC" | "DESC") => void
}

type BodyProps = {
  children: any
  isLoading?: boolean
}

type RowProps = {
  children: any
  onClick: (ev: any) => void
}

type RowColProps = {
  children: any
  onClick?: (ev: any) => void
}

type LoadingProps = {}

type PaginationProps = {
  page: number
  limit: number
  total: number
  onPageChange: (page: number, pageSize: number) => void | null
}

type LimitProps = {
  limit: number
  onLimitChange: (val: number) => void
}

type BottomProps = {
  children: any
}

export const XTable: FC<XTableProps> & XTableExtensions = ({
  children, theme = "", light = false, isLoading = false, style = {}
}) => {

  const childrenWithProps: any = React.Children.map(children, (child: any) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { light, isLoading } as any);
    }
    return child;
  })

  return (
    <>
      <div className={"table-responsive" + (light ? " whited" : "") + (theme != "" ? " " + styles[theme] : "")} style={style}>
        {childrenWithProps}
      </div>
    </>
  )
}

const Table: FC<TableProps> = ({
  children, light, isLoading, order, orderBy, onOrderChange
}) => {

  const childrenWithProps: any = React.Children.map(children, (child: any) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { light, isLoading, order, orderBy, onOrderChange } as any);
    }
    return child;
  })

  return (
    <>
      <table className={"table table-hover text-nowrap" + (light ? " light" : "") + (isLoading ? " isloading" : "")}>
        {childrenWithProps}
      </table>
    </>
  )
}

const Head: FC<HeadProps> & HeadExtensions = ({
  children, light, isLoading, order, orderBy, onOrderChange,
  cols = []
}) => {

  const childrenWithProps: any = React.Children.map(children, (child: any) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { light, isLoading, order, orderBy, onOrderChange } as any);
    }
    return child;
  })

  return (
    <>
      <thead>
        <tr>
          {cols.length == 0 ? childrenWithProps : cols.map(v => 
            <Col order={order} orderBy={orderBy} onOrderChange={onOrderChange}>{v}</Col>
          )}
        </tr>
      </thead>
    </>
  )
}

const Col: FC<HeadColProps> = ({
  children, order, orderBy, onOrderChange
}) => {
  return (
    <>
      <th className="border-top-0 pt-0 pb-2" onClick={() => {
        if (onOrderChange != undefined) {
          onOrderChange(children.toLowerCase().replace(" ", "_").replace(" ", "_"), order == "DESC" ? "ASC" : "DESC")
        }
      }}>{children} {orderBy == children ? <span className="ordered">{order == "DESC" ? <ArrowDownOutlined rev={""} /> : <ArrowUpOutlined rev={""} />}</span> : <></>}</th>
    </>
  )
}

const Body: FC<BodyProps> = ({
  children, isLoading
}) => {
  return (
    <>
      <tbody>
        {isLoading ? <></> : children}
      </tbody>
    </>
  )
}

const Row: FC<RowProps> & RowExtensions = ({
  children, onClick = null
}) => {
  return (
    <>
      <tr onClick={onClick == null ? () => {} : onClick}>
        {children}
      </tr>
    </>
  )
}

const RowCol: FC<RowColProps> = ({
  children, onClick = null
}) => {
  return (
    <>
      <td onClick={onClick == null ? () => {} : onClick} className={"align-middle table-actions"}>
        {children}
      </td>
    </>
  )
}

const Loading: FC<LoadingProps> = () => {
  return (
    <>
      <p style={{
        textAlign: "center",
        padding: "100px 0",
        width: "100%"
      }}>
        <LoadingOutlined spin rev={""} style={{color: "rgba(255, 255, 255, 0.5)", fontSize: "56px"}} />
      </p>
    </>
  )
}

const Pagination: FC<PaginationProps> = ({
  page, limit, total, onPageChange = null
}) => {
  return (
    <>
      <div className="table-bottom__item">
        <div className="tbi__ttl">Cтраница</div>
        <AntPagination 
          className="lighted"
          defaultCurrent={page} 
          current={page} 
          pageSize={limit} 
          total={total} 
          showSizeChanger={false}
          onChange={(page, pageSize) => {
            if (onPageChange != null) {
              onPageChange(page, pageSize)
            }
          }} />
        </div>
    </>
  )
}

const Limit: FC<LimitProps> = ({
  limit, onLimitChange
}) => {
  return (
    <>
      <div className="table-bottom__item">
        <div className="tbi__ttl">Лимит</div>
        <Select
          value={limit}
          defaultValue={limit}
          style={{ width: 120 }}
          onChange={(val, option) => {
            if (onLimitChange != undefined) {
              onLimitChange(val)
            }
          }}
          options={[
            {value: "10", label: "10"},
            {value: "20", label: "20"},
            {value: "30", label: "30"},
            {value: "40", label: "40"},
            {value: "50", label: "50"}
          ]}
        />
      </div>
    </>
  )
}

const Bottom: FC<BottomProps> & BottomExtensions = ({ children }) => {
  return (
    <>
      <div className={"table-bottom " + styles.table_bottom}>
        {children}
      </div>
    </>
  )
}

XTable.Table = Table
XTable.Head = Head
Head.Col = Col
XTable.Body = Body
XTable.Row = Row
Row.Col = RowCol
XTable.Loading = Loading
XTable.Bottom = Bottom
Bottom.Pagination = Pagination
Bottom.Limit = Limit